import React from 'react';

/**
 * Liquid/Glass Badge Component
 * A reusable badge component with modern glassmorphism styling
 */
export const LiquidBadge = ({ 
    children, 
    status, 
    type = 'generic',
    className = '' 
}) => {
    const getBadgeStyle = (status, type) => {
        const baseStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            textTransform: 'capitalize',
            border: '1px solid',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
        };

        // Liquid/Glass effect styles based on status
        switch (type) {
            case 'payment':
                switch (status?.toLowerCase()) {
                    case 'completed':
                    case 'paid':
                    case 'captured':
                    case 'success':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            color: '#16a34a',
                            borderColor: 'rgba(34, 197, 94, 0.3)',
                            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.15)',
                        };
                    case 'pending':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            color: '#d97706',
                            borderColor: 'rgba(245, 158, 11, 0.3)',
                            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.15)',
                        };
                    case 'not_initiated':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(107, 114, 128, 0.1)',
                            color: '#6b7280',
                            borderColor: 'rgba(107, 114, 128, 0.3)',
                            boxShadow: '0 4px 15px rgba(107, 114, 128, 0.15)',
                        };
                    case 'failed':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                            borderColor: 'rgba(239, 68, 68, 0.3)',
                            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)',
                        };
                    case 'refunded':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                            color: '#9333ea',
                            borderColor: 'rgba(168, 85, 247, 0.3)',
                            boxShadow: '0 4px 15px rgba(168, 85, 247, 0.15)',
                        };
                    default:
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(156, 163, 175, 0.1)',
                            color: '#6b7280',
                            borderColor: 'rgba(156, 163, 175, 0.3)',
                            boxShadow: '0 4px 15px rgba(156, 163, 175, 0.15)',
                        };
                }
            case 'gateway':
                switch (status?.toLowerCase()) {
                    case 'razorpay':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: '#2563eb',
                            borderColor: 'rgba(59, 130, 246, 0.3)',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.15)',
                        };
                    case 'paypal':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(0, 112, 210, 0.1)',
                            color: '#0070d2',
                            borderColor: 'rgba(0, 112, 210, 0.3)',
                            boxShadow: '0 4px 15px rgba(0, 112, 210, 0.15)',
                        };
                    case 'stripe':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            color: '#6366f1',
                            borderColor: 'rgba(99, 102, 241, 0.3)',
                            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.15)',
                        };
                    default:
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(107, 114, 128, 0.1)',
                            color: '#6b7280',
                            borderColor: 'rgba(107, 114, 128, 0.3)',
                            boxShadow: '0 4px 15px rgba(107, 114, 128, 0.15)',
                        };
                }
            case 'currency':
                return {
                    ...baseStyle,
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    color: '#7c3aed',
                    borderColor: 'rgba(139, 92, 246, 0.3)',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.15)',
                };
            default:
                switch (status?.toLowerCase()) {
                    case 'success':
                    case 'completed':
                    case 'active':
                    case 'verified':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            color: '#16a34a',
                            borderColor: 'rgba(34, 197, 94, 0.3)',
                            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.15)',
                        };
                    case 'warning':
                    case 'pending':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            color: '#d97706',
                            borderColor: 'rgba(245, 158, 11, 0.3)',
                            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.15)',
                        };
                    case 'error':
                    case 'failed':
                    case 'rejected':
                    case 'non_verified':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                            borderColor: 'rgba(239, 68, 68, 0.3)',
                            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)',
                        };
                    case 'info':
                    case 'submitted':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: '#2563eb',
                            borderColor: 'rgba(59, 130, 246, 0.3)',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.15)',
                        };
                    case 'secondary':
                    case 'draft':
                    case 'inactive':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(107, 114, 128, 0.1)',
                            color: '#6b7280',
                            borderColor: 'rgba(107, 114, 128, 0.3)',
                            boxShadow: '0 4px 15px rgba(107, 114, 128, 0.15)',
                        };
                    default:
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(156, 163, 175, 0.1)',
                            color: '#6b7280',
                            borderColor: 'rgba(156, 163, 175, 0.3)',
                            boxShadow: '0 4px 15px rgba(156, 163, 175, 0.15)',
                        };
                }
        }
    };

    return (
        <span 
            style={getBadgeStyle(status, type)}
            className={`liquid-badge ${className}`}
            data-status={status}
        >
            {children}
        </span>
    );
};

/**
 * Payment Status Badge
 */
export const PaymentStatusBadge = ({ status }) => {
    const getDisplayText = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'paid':
            case 'captured':
                return 'Paid';
            case 'pending':
                return 'Pending';
            case 'not_initiated':
                return 'Not Initiated';
            case 'failed':
                return 'Failed';
            case 'refunded':
                return 'Refunded';
            default:
                return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
        }
    };

    return (
        <LiquidBadge status={status} type="payment">
            {getDisplayText(status)}
        </LiquidBadge>
    );
};

/**
 * Gateway Badge
 */
export const GatewayBadge = ({ gateway }) => {
    const getDisplayText = (gateway) => {
        if (!gateway) return '—';
        return gateway.charAt(0).toUpperCase() + gateway.slice(1);
    };

    return (
        <LiquidBadge status={gateway} type="gateway">
            {getDisplayText(gateway)}
        </LiquidBadge>
    );
};

/**
 * Currency Badge
 */
export const CurrencyBadge = ({ currency }) => {
    if (!currency) return <span>—</span>;

    return (
        <LiquidBadge status={currency} type="currency">
            {currency.toUpperCase()}
        </LiquidBadge>
    );
};

/**
 * Amount Display Component
 * Formats and displays currency amounts with optional badge styling
 */
export const AmountDisplay = ({ amount, currency = 'USD', showBadge = false }) => {
    if (amount === null || amount === undefined) return <span>—</span>;

    const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);

    if (showBadge) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900">{formattedAmount}</span>
                <CurrencyBadge currency={currency} />
            </div>
        );
    }

    return <span className="text-lg font-bold text-gray-900">{formattedAmount}</span>;
};

/**
 * Product Badge
 */
export const ProductBadge = ({ product }) => {
    if (!product) return <span>—</span>;

    const getDisplayText = (product) => {
        if (!product) return '—';
        return product.charAt(0).toUpperCase() + product.slice(1).replace(/_/g, ' ');
    };

    return (
        <LiquidBadge status={product} type="generic">
            {getDisplayText(product)}
        </LiquidBadge>
    );
};

/**
 * Visibility Badge
 */
export const VisibilityBadge = ({ visibility }) => {
    const status = visibility ? 'active' : 'inactive';
    const displayText = visibility ? 'Visible' : 'Hidden';
    
    return (
        <LiquidBadge status={status} type="generic">
            {displayText}
        </LiquidBadge>
    );
};

/**
 * Plan Badge
 */
export const PlanBadge = ({ planId, planName }) => {
    const getPlanDisplay = () => {
        if (planName) return planName;
        if (planId === 3) return 'Ultimate';
        if (planId === 2) return 'Pro';
        if (planId === 1) return 'Free';
        return 'N/A';
    };

    const getPlanStatus = () => {
        if (planId === 3) return 'superuser';
        if (planId === 2) return 'admin';
        return 'secondary';
    };

    return (
        <LiquidBadge status={getPlanStatus()} type="generic">
            {getPlanDisplay()}
        </LiquidBadge>
    );
};

/**
 * Generic Status Badge
 */
export const StatusBadge = ({ status, type = 'generic' }) => {
    const getDisplayText = (status) => {
        if (!status) return '—';
        return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    };

    return (
        <LiquidBadge status={status} type={type}>
            {getDisplayText(status)}
        </LiquidBadge>
    );
};

export default LiquidBadge;
